import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { LogoLockup } from "@/components/Logo";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import GoogleLoginButton from "@/components/GoogleLoginButton";

export default function Signup() {
  const { signup } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const roleParam = new URLSearchParams(loc.search).get("role") || "student";
  const [form, setForm] = useState({ name: "", email: "", password: "", phone: "", role: roleParam });
  const [ownerAgreed, setOwnerAgreed] = useState(false);
  const [studentAgreed, setStudentAgreed] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (form.role === "owner" && !ownerAgreed) {
      toast.error("Please read and agree to the Property Owner Declaration before continuing.");
      return;
    }
    if (form.role === "student" && !studentAgreed) {
      toast.error("Please tick the age & terms declaration to continue.");
      return;
    }
    setLoading(true);
    try {
      const payload = form.role === "owner"
        ? { ...form, owner_declaration_agreed: true, owner_declaration_version: "v1-2026-07" }
        : { ...form, declaration_agreed: true, declaration_version: "v1-2026-08" };
      const u = await signup(payload);
      toast.success(`Welcome to TheHostelNow, ${u.name.split(" ")[0]}!`);
      nav(u.role === "owner" ? "/owner" : "/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Sign up failed");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#FBF5EA] flex">
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          <Link to="/" className="inline-flex items-center mb-8">
            <LogoLockup size={44} tld={false} />
          </Link>
          <h1 className="font-display font-black text-4xl tracking-tighter">Create your account.</h1>
          <p className="mt-2 text-slate-600">Book verified hostels near your coaching, in 30 seconds.</p>

          <form onSubmit={submit} className="mt-8 space-y-4">
            <div>
              <Label className="text-xs tracking-widest uppercase font-bold">I am a</Label>
              <RadioGroup value={form.role} onValueChange={(v) => setForm({ ...form, role: v })} className="mt-2 grid grid-cols-2 gap-2">
                {[["student", "Student"], ["owner", "Hostel Owner"]].map(([v, l]) => (
                  <label key={v} data-testid={`signup-role-${v}`} className={`border rounded-xl px-4 py-3 cursor-pointer flex items-center gap-2 transition-colors ${form.role === v ? "border-orange-800 bg-orange-50" : "border-slate-200 hover:border-slate-300"}`}>
                    <RadioGroupItem value={v} className="border-orange-800" />
                    <span className="text-sm font-semibold">{l}</span>
                  </label>
                ))}
              </RadioGroup>
            </div>

            {form.role === "student" && (
              <div data-testid="signup-google-block">
                <GoogleLoginButton label="Continue with Google" />
                <div className="my-4 flex items-center gap-3">
                  <div className="flex-1 h-px bg-slate-200" />
                  <span className="text-[10px] tracking-widest uppercase font-bold text-slate-400">or sign up with email</span>
                  <div className="flex-1 h-px bg-slate-200" />
                </div>
              </div>
            )}

            <div>
              <Label className="text-xs tracking-widest uppercase font-bold">Full name</Label>
              <Input data-testid="signup-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 h-12" placeholder="Ravi Kumar" />
            </div>
            <div>
              <Label className="text-xs tracking-widest uppercase font-bold">Email</Label>
              <Input data-testid="signup-email" required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1 h-12" placeholder="you@example.com" />
            </div>
            <div>
              <Label className="text-xs tracking-widest uppercase font-bold">Phone</Label>
              <Input data-testid="signup-phone" required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1 h-12" placeholder="98765 43210" />
            </div>
            <div>
              <Label className="text-xs tracking-widest uppercase font-bold">Password</Label>
              <Input data-testid="signup-password" required type="password" minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-1 h-12" />
            </div>

            {form.role === "student" && (
              <div data-testid="student-declaration-block" className="mt-2 rounded-2xl border-2 border-marigold-300 bg-marigold-50 p-4 space-y-3">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[10px] tracking-widest uppercase font-black text-vermilion-700 bg-white border border-marigold-300 rounded-full px-2 py-0.5">Legal</span>
                  <span className="text-sm font-bold text-stone-900">Age &amp; Terms declaration</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  <a
                    href={`${process.env.REACT_APP_BACKEND_URL}/api/legal/terms.pdf`}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="signup-terms-pdf-link"
                    className="inline-flex items-center gap-1.5 text-[11px] font-bold text-vermilion-700 bg-white border border-marigold-300 rounded-full px-2.5 py-1 hover:bg-marigold-100"
                  >
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                    Terms &amp; Conditions (PDF)
                  </a>
                  <a
                    href={`${process.env.REACT_APP_BACKEND_URL}/api/legal/privacy.pdf`}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="signup-privacy-pdf-link"
                    className="inline-flex items-center gap-1.5 text-[11px] font-bold text-vermilion-700 bg-white border border-marigold-300 rounded-full px-2.5 py-1 hover:bg-marigold-100"
                  >
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                    Privacy Policy (PDF)
                  </a>
                </div>
                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    data-testid="student-declaration-checkbox"
                    type="checkbox"
                    checked={studentAgreed}
                    onChange={(e) => setStudentAgreed(e.target.checked)}
                    className="mt-1 w-4 h-4 accent-vermilion-600 flex-shrink-0"
                  />
                  <span className="text-xs text-stone-800 leading-relaxed">
                    I confirm that I am <b>18 years of age or above</b>, or that my <b>parent or guardian has given consent</b> for me to register and use TheHostelNow.com platform. I agree to the <b>Terms and Conditions</b> and <b>Privacy Policy</b>.
                  </span>
                </label>
                {studentAgreed && (
                  <div className="text-[10px] font-mono text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md px-2 py-1 inline-block">
                    ✓ Declaration will be recorded at {new Date().toLocaleString("en-IN")}
                  </div>
                )}
              </div>
            )}

            {form.role === "owner" && (
              <div data-testid="owner-declaration-block" className="mt-2 rounded-2xl border-2 border-marigold-300 bg-marigold-50 p-4 space-y-3">
                <div className="flex items-center gap-2 flex-wrap justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] tracking-widest uppercase font-black text-vermilion-700 bg-white border border-marigold-300 rounded-full px-2 py-0.5">Legal</span>
                    <span className="text-sm font-bold text-stone-900">Property Owner Declaration</span>
                  </div>
                  <button
                    type="button"
                    data-testid="signup-declaration-pdf-btn"
                    onClick={() => {
                      const url = `${process.env.REACT_APP_BACKEND_URL}/api/public/declaration-preview.pdf`;
                      const w = window.open(url, "_blank", "noopener,noreferrer");
                      if (!w) {
                        // Popup blocked — fall back to direct navigation (same tab)
                        toast.info("Opening declaration PDF…");
                        window.location.href = url;
                      }
                    }}
                    className="inline-flex items-center gap-1.5 text-[11px] font-bold text-vermilion-700 bg-white border border-marigold-300 rounded-full px-2.5 py-1 hover:bg-marigold-100 transition-colors"
                    title="Open the Property Owner Declaration as a PDF"
                  >
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="12" y1="18" x2="12" y2="12"/><polyline points="9 15 12 18 15 15"/></svg>
                    Open PDF
                  </button>
                </div>
                <div
                  data-testid="owner-declaration-text"
                  className="max-h-48 overflow-y-auto text-[13px] leading-relaxed text-stone-800 bg-white border border-marigold-200 rounded-xl p-3 space-y-2"
                >
                  <p>By ticking the box below and creating a Property Owner account on <b>TheHostelNow.com</b>, I hereby declare, warrant and confirm the following:</p>
                  <p><b>1. Legal authority.</b> I am the lawful owner of the property/properties I intend to list, or I have a valid registered lease, licence, or Power of Attorney granting me the legal right to sub-let, operate, and commercially exploit the property as a paying-guest / student hostel.</p>
                  <p><b>2. Compliance.</b> The property meets all applicable local, state, and municipal regulations, including fire safety, sanitation, building permissions, GST/tax registration (where applicable), and any state-specific PG or lodging licences.</p>
                  <p><b>3. Truthful listings.</b> Every photograph, amenity, price, occupancy count, and address I upload will be accurate. I will not misrepresent facilities or availability.</p>
                  <p><b>4. Guest safety.</b> I will maintain proper guest-registry records, verify guest identity at check-in against the Aadhaar-verified booking, and comply with all applicable regulations regarding tenant/lodger records.</p>
                  <p><b>5. Indemnification.</b> I shall solely be responsible for any dispute, claim, damage or liability arising from the operation of my listed property, and I fully indemnify TheHostelNow.com, its founders, employees, contractors and users from any such claim.</p>
                  <p><b>6. Right of removal.</b> TheHostelNow.com reserves the right, without prior notice, to delist any property that is found to violate the terms of this declaration or any applicable law.</p>
                  <p>I understand this declaration is legally binding and my digital acceptance below shall constitute my signature. My agreement will be recorded with an IP-adjacent timestamp and declaration version for auditing.</p>
                </div>
                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    data-testid="owner-declaration-checkbox"
                    type="checkbox"
                    checked={ownerAgreed}
                    onChange={(e) => setOwnerAgreed(e.target.checked)}
                    className="mt-1 w-4 h-4 accent-vermilion-600"
                  />
                  <span className="text-xs text-stone-800">
                    I have read and agree to the <b>Property Owner Declaration</b> above.
                    <span className="block text-slate-500 mt-0.5">Tip: tap <b>"Open PDF"</b> above for a full-screen read.</span>
                  </span>
                </label>
                {ownerAgreed && (
                  <div className="text-[10px] font-mono text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md px-2 py-1 inline-block">
                    ✓ Agreement will be recorded at {new Date().toLocaleString("en-IN")}
                  </div>
                )}
              </div>
            )}

            <Button data-testid="signup-submit" type="submit" disabled={loading || (form.role === "owner" && !ownerAgreed) || (form.role === "student" && !studentAgreed)} className="w-full h-12 rounded-full bg-orange-800 hover:bg-orange-800 text-white active:scale-95 transition-transform disabled:opacity-50 disabled:cursor-not-allowed">
              {loading ? "Creating…" : form.role === "owner" && !ownerAgreed ? "Agree to declaration first" : form.role === "student" && !studentAgreed ? "Tick the declaration to continue" : "Create account"}
            </Button>
          </form>

          <div className="mt-6 text-sm text-slate-600">Already registered? <Link to="/login" className="text-orange-800 font-semibold hover:underline">Sign in</Link></div>
        </div>
      </div>
      <div className="hidden lg:block flex-1 bg-amber-600 relative overflow-hidden">
        <img src="https://images.unsplash.com/photo-1709805619372-40de3f158e83?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODl8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBjbGVhbiUyMGhvc3RlbCUyMGJ1bmslMjBiZWQlMjBkb3JtfGVufDB8fHx8MTc4MzY1OTk4NHww&ixlib=rb-4.1.0&q=85" className="w-full h-full object-cover opacity-80" alt="" />
      </div>
    </div>
  );
}
